import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import LogoLoader from 'components/Loader/LogoLoader';
import APIErrorMascot from 'components/Mascot/APIErrorMascot';
import { initialJobFormData } from 'containers/JobsTasks/shared/JobForm/schema';
import { useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useFetchJobById } from 'hooks/jobs/useFetchJobById';
import { hasCustomFieldAccess, transformSavedCustomFieldsToFormData } from 'utils/customFieldsUtils';

import JobFormTabs from '../shared/JobForm/JobFormTabs';

const EditJob: React.FC<unknown> = () => {
  const [formData, setFormData] = useState<typeof initialJobFormData>(initialJobFormData);

  const { id } = useParams();

  const hasAccess = hasCustomFieldAccess();

  const {
    status: customFieldStatus,
    fetchStatus: customFieldFetchStatus,
    data: customFieldsResData,
  } = useFetchPaginatedCustomFields(
    {
      page: 1,
      page_size: 500,
      module: 'jobs',
      active: '1',
    },
    {
      enabled: hasAccess,
    }
  );

  const { data, status } = useFetchJobById(id!);

  useEffect(() => {
    if (status === 'success') {
      const fetchedFormData = data?.payload;

      const reversedCoordinateData = fetchedFormData?.geofence_coordinates.map(
        (coordinate: [number, number]) => [...coordinate].reverse() as [number, number]
      );

      let savedCustomFields = {};

      if (customFieldStatus === 'success' && customFieldsResData?.payload) {
        savedCustomFields = transformSavedCustomFieldsToFormData(
          customFieldsResData?.payload,
          fetchedFormData?.custom_fields
        );
      }

      const formData: typeof initialJobFormData = {
        ...initialJobFormData,
        id: fetchedFormData.id,
        name: fetchedFormData.name,
        job_code: fetchedFormData.jobCode,
        description: fetchedFormData?.description || '',
        users: fetchedFormData?.assigned_users || [],
        requireGeofence: fetchedFormData.requireGeofence ? ['yes'] : [],
        rate: fetchedFormData?.rate || '',
        track_mileage: fetchedFormData.trackMileage ? ['yes'] : [],
        radius: fetchedFormData?.radius || '',
        contactRequired: fetchedFormData?.contactRequired ? ['yes'] : [],
        contact_name: fetchedFormData?.contactInfo?.contactName || '',
        contact_phone: fetchedFormData?.contactInfo?.contactPhone || '',
        contact_mobile: fetchedFormData?.contactInfo?.contactMobile || '',
        contact_email: fetchedFormData?.contactInfo?.contactEmail || '',
        street: fetchedFormData?.address?.street || '',
        state: fetchedFormData?.address?.state || '',
        city: fetchedFormData?.address?.city || '',
        zip_code: fetchedFormData?.address?.zipCode || '',
        country: fetchedFormData?.address?.country || '',
        on_arrival: fetchedFormData?.on_arrival ? '' + fetchedFormData.on_arrival : '0',
        on_departure: fetchedFormData?.on_departure ? '' + fetchedFormData.on_departure : '0',
        latitude: fetchedFormData?.latitude || initialJobFormData.latitude,
        longitude: fetchedFormData?.longitude || initialJobFormData.longitude,
        geofenceMembers: fetchedFormData?.geofence_members || [],
        custom_geofence: fetchedFormData?.custom_geofence ? '1' : '0',
        isPrimaryAddress: [
          (fetchedFormData?.latitude || initialJobFormData.latitude) &&
          (fetchedFormData?.longitude || initialJobFormData.longitude)
            ? 'yes'
            : 'no',
        ],
        geofence_features: {
          type: 'Feature',
          properties: {},
          geometry: {
            coordinates: [reversedCoordinateData],
            type: 'Polygon',
          },
        },
        feature_collection: reversedCoordinateData.length
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  id: '48ab816c069b53bfffe603b9c82db88d',
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    coordinates: [reversedCoordinateData],
                    type: 'Polygon',
                  },
                },
              ],
            }
          : {},
        ...savedCustomFields,
      };

      setFormData(formData);
    }
  }, [status, data, customFieldStatus, customFieldsResData]);

  if (status === 'loading' || customFieldFetchStatus === 'fetching') return <LogoLoader />;
  if (status === 'error') return <APIErrorMascot />;

  return <JobFormTabs formData={formData} customFieldsData={hasAccess ? customFieldsResData?.payload || [] : null} />;
};

export default EditJob;
